/**
 * SafeHTML — small helper module that wraps DOMPurify with a strict
 * allow-list, so we can render lightly-formatted text (bold, code,
 * headings, line breaks, links) coming from LLM output OR user input
 * WITHOUT opening the door to `<script>`, event handlers, or
 * javascript: URLs.
 *
 * Security context (SEC-003, 2026-07-11):
 * Historically the app used `dangerouslySetInnerHTML` after applying
 * regex-based markdown-lite transforms on raw text. That meant a user
 * (or an LLM prompt-injection) could ship `<img src=x onerror=...>`
 * straight into their own DOM and read the session token out of
 * localStorage. Now every such render must go through `sanitize()`
 * below or the `<SafeHTML/>` component.
 *
 * We ALSO HTML-escape the raw input BEFORE the format-regexes run —
 * so any `<` or `>` a user typed is neutralised before we add our
 * own safe tags. DOMPurify then acts as the last-line defence.
 */
import DOMPurify from 'dompurify';
import React from 'react';

// Allowed tags after formatting — nothing that can execute JS.
const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'code', 'pre', 'br', 'p', 'span', 'div', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'blockquote', 'hr'];
const ALLOWED_ATTR = ['class', 'href', 'target', 'rel'];

// Force any anchor to open in a new tab with `rel="noopener noreferrer"`
// so a rendered link can't rewrite `window.opener`.
if (typeof window !== 'undefined' && DOMPurify.addHook) {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      // Block anything that isn't http(s)://, mailto: or a plain relative path.
      const href = node.getAttribute('href') || '';
      const safe = /^(https?:\/\/|mailto:|\/|#)/i.test(href);
      if (!safe) node.removeAttribute('href');
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

/**
 * Escape the four HTML-special characters. Call this on RAW input
 * BEFORE running any format regexes so `<script>` becomes text.
 */
export function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Sanitize a string of HTML (that we produced ourselves through the
 * escape → format-regex pipeline) with a strict allow-list.
 */
export function sanitize(html) {
  return DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link', 'meta', 'svg', 'math'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'srcset', 'formaction'],
  });
}

/**
 * Drop-in replacement for `<span dangerouslySetInnerHTML={{__html:x}}/>`
 * that always runs the input through DOMPurify.
 */
export function SafeHTML({ html, as: Tag = 'span', className, ...rest }) {
  const clean = React.useMemo(() => sanitize(html), [html]);
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: clean }} {...rest} />;
}
